import { DataTable } from "@/components/DataTable/dataTable.component";
import UserApprovalRejection from "@/components/UserManagementComponents/userApprovalRejection.component";
import { LANGUAGES } from "@/constants/util.constants";
import { fetchAdminUsersList } from "@/models/socket/admin.model";
import { Button } from "@/shadcn/components/ui/button";
import { AdminUserType, RoleName } from "@/types";
import { formatDatePretty } from "@/utils/utils.util";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import React, { useEffect, useState } from "react";

type AdminManagementMainTableProps = {};

const AdminManagementMainTable: React.FC<
  AdminManagementMainTableProps
> = () => {
  // Using state to allow editing of user data in the table
  const [users, setUsers] = useState<AdminUserType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Required for EditableTable: tracks which row is being edited

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      setError(null);

      const response = await fetchAdminUsersList();
      if (response.rStatus === "success") {
        setUsers(response.rData);
      } else {
        setError(response.rData);
      }
      setLoading(false);
    }

    fetchUsers();
  }, []);

  // Handler for updating a user's role
  function handleRoleChange(
    userId: string,
    roleAction: "approve" | "block" | "admin"
  ) {
    const newRole: RoleName =
      roleAction === "approve"
        ? "approved_user"
        : roleAction === "block"
          ? "blocked_user"
          : "admin";

    // Using state update to trigger re-render after role change
    setUsers((prev) =>
      prev.map((user) =>
        user.id === userId ? { ...user, roleName: newRole } : user
      )
    );
  }

  // Defining columns for the users table
  const columns: ColumnDef<AdminUserType>[] = [
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          ID
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "id",
      enableSorting: true,
      enableHiding: false,
      size: 80,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Name
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "fullName",
      enableSorting: true,
      enableHiding: false,
      size: 220,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Email
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "email",
      enableSorting: true,
      enableHiding: false,
      size: 240,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Phone
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "phoneNumber",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Designation
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "designation",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Role
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "roleName",
      enableSorting: true,
      enableHiding: false,
      size: 110,
      // Using a select input to allow editing of the user's role
      cell: ({ row }) => {
        const user = row.original;
        const niceRoleName =
          user.roleName === "super_admin"
            ? "Super Admin"
            : user.roleName === "admin"
              ? "Admin"
              : user.roleName === "approved_user"
                ? "Approved User"
                : user.roleName === "blocked_user"
                  ? "Blocked User"
                  : "New User";
        return niceRoleName;
      },
      meta: {
        enableEditing: true,
      },
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Language
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "preferredLanguage",
      enableSorting: true,
      enableHiding: false,
      size: 180,
      cell: ({ cell }) => {
        const language = cell.getValue() as string | null;
        // Null check is required here to avoid calling toLowerCase() on null, which would cause a runtime error.
        const foundLanguage = language
          ? LANGUAGES.find(
              (lang) => lang.code.toLowerCase() === language.toLowerCase()
            )
          : undefined;
        return <span>{foundLanguage ? foundLanguage.name : "N/A"}</span>;
      },
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Last Login
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "lastLogin",
      enableSorting: true,
      enableHiding: false,
      size: 140,
      cell: ({ cell }) => {
        const lastLogin = cell.getValue() as string | null;
        // Format the last login date if available
        if (!lastLogin) {
          return <span>N/A</span>;
        }
        const date = formatDatePretty(lastLogin, true, true);
        return <span>{date ? date : "N/A"}</span>;
      },
    },
    // {
    //   header: "Note",
    //   accessorKey: "note",
    //   enableSorting: true,
    //   enableHiding: false,
    //   // Show N/A for null/empty notes for consistency
    //   cell: ({ cell }) =>
    //     cell.getValue() ? (
    //       <HoverCard openDelay={200} closeDelay={200}>
    //         <HoverCardTrigger asChild>
    //           <Button variant="ghost">
    //             <Notebook className="h-5 w-5" />
    //           </Button>
    //         </HoverCardTrigger>
    //         <HoverCardContent
    //           className="max-h-80 max-w-50 overflow-y-auto rounded-md bg-neutral-900 p-1 text-sm whitespace-break-spaces"
    //           style={{
    //             scrollbarWidth: "none",
    //           }}
    //         >
    //           <div className="flex justify-between gap-4">
    //             <span className="text-sm">
    //               {String(cell.getValue() || "N/A")}
    //             </span>
    //           </div>
    //         </HoverCardContent>
    //       </HoverCard>
    //     ) : (
    //       <></>
    //     ),
    // },
    {
      header: () => (
        <Button variant="ghost" className="!bg-transparent !p-0">
          Actions
        </Button>
      ),
      accessorKey: "action",
      enableSorting: false,
      enableHiding: false,
      size: 80,
      cell: ({ row }) => {
        const user = row.original;

        return (
          <UserApprovalRejection
            userFullName={user.fullName}
            userID={user.id}
            userRoleName={user.roleName}
            updateRole={handleRoleChange}
          />
        );
      },
    },
  ];

  return loading ? (
    <div className="py-4">
      <div className="flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-3 border-white"></div>
      </div>
      <div className="flex items-center justify-center">
        <h2 className="mt-2 text-xl text-white">Loading Users...</h2>
      </div>
    </div>
  ) : error ? (
    <div className="mt-4 text-center text-red-500">
      <p>Error fetching users: {error}</p>
    </div>
  ) : (
    <div className="mx-4">
      <DataTable
        columns={columns}
        data={users}
        initialState={{
          sorting: [
            {
              id: "id",
              desc: false,
            },
          ],
        }}
        includeSelectRow={false}
        disableColumnSelection={true}
      />
    </div>
  );
};

export default AdminManagementMainTable;
